// Shared PWA-install logic and content.
// Single source of truth for detection, event wiring, and per-mode content
// (title / description / instructions / CTA). Consumed by both the one-shot
// popup and the inline home banner + modal.
import { useEffect, useState } from "react";
import { X, Share, Plus, Download, Chrome, Copy, ExternalLink, MoreVertical } from "lucide-react";

// ---- localStorage keys (SAME as before to preserve user dismissals) --------
export const SAMSUNG_KEY   = "cn_install_samsung_dismissed";
export const CHROME_KEY    = "cn_install_chrome_dismissed";
export const IOS_KEY       = "cn_install_ios_dismissed";
export const LEGACY_KEY    = "cn_install_dismissed";
export const COMPLETED_KEY = "cn_install_completed";
// New: marks that the one-shot popup has been rendered on this device (any
// outcome). Never used to hide the banner — only to hide the popup on 2nd+ visits.
export const POPUP_SHOWN_KEY = "cn_install_popup_shown";

export const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days (banner dismissals)
export const IOS_DISMISS_TTL_MS = 180 * 24 * 60 * 60 * 1000; // 180 days (A3 safety net for iOS)
export const CHROME_MANUAL_TIMEOUT_MS = 3500;

export const OFFICIAL_URL = "https://www.condominionow.it";

// Cross-tab / cross-context signalling channel (A2): when any client of this
// origin detects it is running as an installed PWA it broadcasts "installed"
// so other open tabs of the same context can mark completion without a
// reload. Note: iOS isolates Safari and the installed PWA into separate
// contexts, so this signal does NOT cross that boundary — the A3 long TTL is
// the safety net there.
export const INSTALL_CHANNEL = "cn-install";

// ---- Environment detection --------------------------------------------------
export function isIOS() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    const isIPhoneish = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isIPadOS = ua.includes("Mac") && "ontouchend" in document;
    return isIPhoneish || isIPadOS;
}

export function isAndroid() {
    if (typeof window === "undefined") return false;
    return /Android/i.test(window.navigator.userAgent || "");
}

export function isSamsungInternet() {
    if (typeof window === "undefined") return false;
    return /SamsungBrowser\//i.test(window.navigator.userAgent || "");
}

// Covers Android WebAPK, iOS standalone, and Desktop PWA (Chrome / Edge).
export function isStandalone() {
    if (typeof window === "undefined") return false;
    if (window.navigator.standalone === true) return true;
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    return false;
}

export function isCompleted() {
    try { return localStorage.getItem(COMPLETED_KEY) === "1"; } catch { return false; }
}

export function isPopupShown() {
    try { return !!localStorage.getItem(POPUP_SHOWN_KEY); } catch { return false; }
}

export function markPopupShown() {
    try { localStorage.setItem(POPUP_SHOWN_KEY, String(Date.now())); } catch { /* ignore */ }
}

export function markCompleted() {
    try { localStorage.setItem(COMPLETED_KEY, "1"); } catch { /* ignore */ }
}

// A4: called when we detect (via getInstalledRelatedApps) that the PWA has
// been uninstalled — so the popup/banner return automatically on the next
// browser open. Only runs where the API is available (Android Chromium
// family + Desktop Chromium). On iOS Safari nothing calls this (Apple does
// not expose an equivalent API — we accept the trade-off).
export function unmarkCompleted() {
    try { localStorage.removeItem(COMPLETED_KEY); } catch { /* ignore */ }
}

// Best-effort check that the PWA is actually installed for this origin.
// Resolves to `null` when the API is unavailable — callers must fall back to
// the localStorage `cn_install_completed` flag in that case (iOS Safari,
// older browsers).
export async function verifyInstalledViaApi() {
    if (typeof navigator === "undefined") return null;
    if (typeof navigator.getInstalledRelatedApps !== "function") return null;
    try {
        const apps = await navigator.getInstalledRelatedApps();
        return Array.isArray(apps) && apps.length > 0;
    } catch { return null; }
}

export function isDismissedTTL(key, ttlMs = DISMISS_TTL_MS) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return false;
        if (raw === "1") return true;
        const ts = parseInt(raw, 10);
        if (!ts) return false;
        return (Date.now() - ts) < ttlMs;
    } catch { return false; }
}

export function persistDismissal(key) {
    try { localStorage.setItem(key, String(Date.now())); } catch { /* ignore */ }
}

// ---- Shared install state hook ---------------------------------------------
// Resolves the appropriate mode for the current browser/device and exposes:
//   - mode: "ios" | "chrome-native" | "chrome-manual" | "samsung" | null
//   - deferredPrompt: BeforeInstallPromptEvent | null (Chrome only)
//   - installed: boolean (live — flips when appinstalled fires)
//   - installing, showSamsungFallback, copied, triggerNativeInstall, openInChrome, copyLink
//
// Multiple components may call this hook concurrently: each wires its own
// listeners; they don't interfere because state is per-hook.
export function useInstallState({
    // If true, honour per-browser dismissal TTL keys (used by the one-shot
    // popup). If false, the mode resolves regardless of dismissal TTL (used
    // by the home banner, which only cares about "installed" and "shown-once").
    respectDismissTTL = true,
    // If true, only resolve a mode after the manual-fallback timer has
    // elapsed (Android Chrome).
    androidChromeManualFallback = true,
} = {}) {
    const [mode, setMode] = useState(null);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installing, setInstalling] = useState(false);
    const [showSamsungFallback, setShowSamsungFallback] = useState(false);
    const [copied, setCopied] = useState(false);
    const [installed, setInstalled] = useState(() => isStandalone() || isCompleted());

    // ---- A2: cross-context / cross-tab install detection --------------------
    // 1) If THIS context is already a standalone PWA (Android WebAPK, iOS
    //    standalone, Desktop PWA), persist completion so any subsequent
    //    non-standalone load in the same storage context skips prompts too.
    // 2) Broadcast the "installed" event on a same-origin BroadcastChannel so
    //    other open tabs of the same context react immediately.
    // 3) Also notify the service worker, which forwards to other clients (for
    //    browsers that don't propagate BroadcastChannel across window types).
    // 4) Listen on both channels so any other tab / window flips our state
    //    without a reload.
    // 5) A4: if the browser exposes getInstalledRelatedApps() and reports the
    //    PWA is NOT installed, clear cn_install_completed so a previously
    //    installed-then-uninstalled app returns to the popup/banner flow.
    //    Only runs when we are NOT in a standalone context. iOS Safari does
    //    not expose this API — the flag stays untouched there.
    // NOTE: Apple isolates the Safari and installed-PWA contexts, so on iOS
    // this only syncs across multiple Safari tabs (not Safari ↔ PWA). The
    // IOS_DISMISS_TTL_MS (A3) covers that gap.
    useEffect(() => {
        if (isStandalone() && !isCompleted()) markCompleted();

        // A4 — Verify installation status via getInstalledRelatedApps. If the
        // browser says the app is NOT installed while we had cn_install_completed
        // set, the user has uninstalled: clear the flag so prompts return.
        if (!isStandalone() && isCompleted()) {
            (async () => {
                const isInstalled = await verifyInstalledViaApi();
                // isInstalled === null → API unavailable (iOS/older) → keep flag.
                // isInstalled === true → still installed → keep flag.
                // isInstalled === false → uninstalled → clear + re-enable prompts.
                if (isInstalled === false) {
                    unmarkCompleted();
                    setInstalled(false);
                }
            })();
        }

        let bc = null;
        try {
            if (typeof BroadcastChannel === "function") {
                bc = new BroadcastChannel(INSTALL_CHANNEL);
                if (isStandalone()) {
                    try { bc.postMessage({ type: "installed", ts: Date.now() }); } catch { /* noop */ }
                }
                bc.onmessage = (ev) => {
                    if (ev?.data?.type === "installed") {
                        markCompleted();
                        setInstalled(true);
                        setMode(null);
                    }
                };
            }
        } catch { /* BroadcastChannel unsupported — safe no-op */ }

        // Tell the service worker (if any) that this window is standalone;
        // the SW then relays "cn:pwa-installed" to any browser tab of the
        // same origin so the banner disappears without a reload.
        if (isStandalone()) {
            try {
                navigator.serviceWorker?.ready?.then((reg) => {
                    reg.active?.postMessage({ type: "cn:pwa-installed" });
                }).catch(() => { /* noop */ });
            } catch { /* noop */ }
        }

        // Service-worker-forwarded install signal.
        const onSwMsg = (ev) => {
            if (ev?.data?.type === "cn:pwa-installed") {
                markCompleted();
                setInstalled(true);
                setMode(null);
            }
        };
        try { navigator.serviceWorker?.addEventListener?.("message", onSwMsg); } catch { /* noop */ }

        return () => {
            try { bc?.close(); } catch { /* noop */ }
            try { navigator.serviceWorker?.removeEventListener?.("message", onSwMsg); } catch { /* noop */ }
        };
    }, []);

    useEffect(() => {
        if (installed) return;

        // ---- Samsung Internet on Android ----
        if (isSamsungInternet() && isAndroid()) {
            if (respectDismissTTL && isDismissedTTL(SAMSUNG_KEY)) return;
            const t = setTimeout(() => setMode("samsung"), 700);
            return () => clearTimeout(t);
        }

        // ---- iOS Safari ----
        if (isIOS()) {
            if (respectDismissTTL && (
                isDismissedTTL(IOS_KEY, IOS_DISMISS_TTL_MS) ||
                isDismissedTTL(LEGACY_KEY, IOS_DISMISS_TTL_MS)
            )) return;
            const iosTimer = setTimeout(() => {
                if (isStandalone()) return;
                setMode((m) => m || "ios");
            }, 900);
            return () => clearTimeout(iosTimer);
        }

        // ---- Chromium (Chrome / Edge / Brave / etc.) ----
        if (respectDismissTTL && isDismissedTTL(CHROME_KEY)) return;

        if (typeof window !== "undefined" && window.__cnDeferredPrompt) {
            setDeferredPrompt(window.__cnDeferredPrompt);
            setMode("chrome-native");
        }

        const onBip = (e) => {
            try { e.preventDefault?.(); } catch { /* noop */ }
            setDeferredPrompt(e);
            setMode("chrome-native");
        };
        const onCustom = () => {
            if (typeof window !== "undefined" && window.__cnDeferredPrompt) {
                setDeferredPrompt(window.__cnDeferredPrompt);
                setMode("chrome-native");
            }
        };
        const onInstalled = () => {
            markCompleted();
            setInstalled(true);
            setMode(null);
            setDeferredPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onBip);
        window.addEventListener("cn:install-prompt-ready", onCustom);
        window.addEventListener("appinstalled", onInstalled);

        // Also watch display-mode changes so the banner disappears immediately
        // when the user completes the install without the appinstalled event
        // firing on this page (some browsers only dispatch it to the new PWA).
        let mql;
        const onDisplayModeChange = (ev) => {
            if (ev.matches) {
                markCompleted();
                setInstalled(true);
                setMode(null);
            }
        };
        try {
            mql = window.matchMedia("(display-mode: standalone)");
            mql.addEventListener?.("change", onDisplayModeChange);
        } catch { /* older browsers */ }

        let chromeManualTimer = null;
        if (isAndroid() && androidChromeManualFallback) {
            chromeManualTimer = setTimeout(() => {
                if (isStandalone() || isCompleted()) return;
                if (respectDismissTTL && isDismissedTTL(CHROME_KEY)) return;
                setMode((m) => m || "chrome-manual");
            }, CHROME_MANUAL_TIMEOUT_MS);
        }

        return () => {
            window.removeEventListener("beforeinstallprompt", onBip);
            window.removeEventListener("cn:install-prompt-ready", onCustom);
            window.removeEventListener("appinstalled", onInstalled);
            try { mql?.removeEventListener?.("change", onDisplayModeChange); } catch { /* noop */ }
            if (chromeManualTimer) clearTimeout(chromeManualTimer);
        };
    }, [installed, respectDismissTTL, androidChromeManualFallback]);

    const triggerNativeInstall = async () => {
        if (!deferredPrompt) return null;
        setInstalling(true);
        try {
            await deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            setDeferredPrompt(null);
            try { window.__cnDeferredPrompt = null; } catch { /* noop */ }
            return choice; // { outcome: "accepted" | "dismissed" }
        } catch {
            setDeferredPrompt(null);
            return null;
        } finally {
            setInstalling(false);
        }
    };

    const openInChrome = () => {
        const intentUrl =
            "intent://www.condominionow.it/#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=" +
            encodeURIComponent(OFFICIAL_URL) +
            ";end";
        const started = Date.now();
        const fallbackTimer = setTimeout(() => {
            if (Date.now() - started >= 1500) setShowSamsungFallback(true);
        }, 1600);
        try { window.location.href = intentUrl; }
        catch { setShowSamsungFallback(true); }
        const onVis = () => {
            if (document.visibilityState === "hidden") {
                clearTimeout(fallbackTimer);
                document.removeEventListener("visibilitychange", onVis);
            }
        };
        document.addEventListener("visibilitychange", onVis);
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(OFFICIAL_URL);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch { /* ignore */ }
    };

    return {
        mode,
        setMode,
        deferredPrompt,
        installed,
        installing,
        showSamsungFallback,
        setShowSamsungFallback,
        copied,
        triggerNativeInstall,
        openInChrome,
        copyLink,
    };
}

// ---- Shared content ---------------------------------------------------------
// Titles, descriptions, and instruction blocks. Consumed by both the popup
// and the modal. This is the ONLY place where per-mode copy lives.
export function InstallEyebrow({ mode }) {
    return (
        <div className="text-xs uppercase tracking-widest text-primary font-semibold">
            {mode === "samsung" ? "Consigliato" : "Installa l'app"}
        </div>
    );
}

export function InstallTitle({ mode }) {
    return (
        <div className="mt-0.5 font-serif text-lg font-semibold text-slate-900 leading-tight">
            {mode === "samsung" && "Installa CondominioNow con Google Chrome"}
            {mode === "chrome-native" && "Installa CondominioNow"}
            {mode === "chrome-manual" && "Installa dal menu di Chrome"}
            {mode === "ios" && "CondominioNow sulla tua Home"}
        </div>
    );
}

export function InstallLead({ mode }) {
    return (
        <p className="mt-1 text-xs text-slate-600 leading-relaxed">
            {mode === "chrome-native" && "Porta CondominioNow sempre con te. Installa gratuitamente l'app sul tuo dispositivo."}
            {mode === "chrome-manual" && "Apri il menu di Google Chrome e seleziona \"Installa app\" oppure \"Aggiungi a schermata Home\"."}
            {mode === "ios" && "Accedi più velocemente e in modalità a schermo intero."}
            {mode === "samsung" && "Su alcune versioni di Samsung Internet l'installazione delle web app può essere bloccata per un problema di compatibilità del browser. Per installare CondominioNow in modo corretto e sicuro, apri questa pagina con Google Chrome."}
        </p>
    );
}

// ---- Per-mode action block (CTA + instructions) ----------------------------
// Renders the CTA, instructions and secondary actions specific to `mode`.
// `dismissLabel` and `onDismiss` control the secondary close/skip button.
export function InstallActions({
    mode,
    deferredPrompt,
    installing,
    showSamsungFallback,
    setShowSamsungFallback,
    copied,
    onNativeInstall,
    onOpenChrome,
    onCopyLink,
    onDismiss,
    dismissLabel = "Non ora",
    testIdPrefix = "install-banner",
}) {
    if (mode === "chrome-native") {
        return (
            <div className="mt-4 border-t border-slate-100 pt-3">
                <button
                    type="button"
                    onClick={onNativeInstall}
                    disabled={installing || !deferredPrompt}
                    data-testid={`${testIdPrefix}-install-btn`}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-white font-semibold text-sm py-2.5 hover:bg-primary/90 transition disabled:opacity-70"
                >
                    <Download className="size-4" strokeWidth={2.5} />
                    {installing ? "Installazione…" : "Installa CondominioNow"}
                </button>
                {onDismiss && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        data-testid={`${testIdPrefix}-later`}
                        className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700 transition py-1.5"
                    >
                        {dismissLabel}
                    </button>
                )}
            </div>
        );
    }

    if (mode === "chrome-manual") {
        return (
            <div className="mt-4 border-t border-slate-100 pt-3">
                <ol className="space-y-1.5 text-[13px] text-slate-700">
                    <li className="flex items-start gap-2">
                        <span className="size-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">1</span>
                        <span className="flex items-center gap-1.5 flex-wrap">
                            Tocca il menu <MoreVertical className="size-4 text-primary inline shrink-0" strokeWidth={2.5} /> in alto a destra
                        </span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="size-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">2</span>
                        <span>Seleziona <span className="font-semibold">"Installa app"</span> o <span className="font-semibold">"Aggiungi a schermata Home"</span>.</span>
                    </li>
                </ol>
                {onDismiss && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        data-testid={`${testIdPrefix}-later`}
                        className="mt-3 w-full text-xs text-slate-500 hover:text-slate-700 transition py-1.5"
                    >
                        {dismissLabel}
                    </button>
                )}
            </div>
        );
    }

    if (mode === "ios") {
        return (
            <>
                <ol className="mt-3 space-y-1.5 text-[13px] text-slate-700 border-t border-slate-100 pt-3">
                    <li className="flex items-center gap-2">
                        <span className="size-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0">1</span>
                        <span className="flex items-center gap-1.5">
                            Tocca <Share className="size-4 text-primary inline shrink-0" strokeWidth={2} /> <span className="font-semibold">Condividi</span>
                        </span>
                    </li>
                    <li className="flex items-center gap-2">
                        <span className="size-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0">2</span>
                        <span className="flex items-center gap-1.5">
                            Scegli <Plus className="size-4 text-primary inline shrink-0" strokeWidth={2.5} /> <span className="font-semibold">Aggiungi alla schermata Home</span>
                        </span>
                    </li>
                </ol>
                {onDismiss && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        data-testid={`${testIdPrefix}-later`}
                        className="mt-3 w-full text-xs text-slate-500 hover:text-slate-700 transition py-1.5"
                    >
                        {dismissLabel}
                    </button>
                )}
            </>
        );
    }

    if (mode === "samsung") {
        return (
            <div className="mt-4 border-t border-slate-100 pt-3 space-y-2">
                {!showSamsungFallback ? (
                    <>
                        <button
                            type="button"
                            onClick={onOpenChrome}
                            data-testid={`${testIdPrefix}-open-chrome`}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-white font-semibold text-sm py-2.5 hover:bg-primary/90 transition"
                        >
                            <Chrome className="size-4" strokeWidth={2.5} /> Apri con Chrome
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowSamsungFallback(true)}
                            data-testid={`${testIdPrefix}-show-instructions`}
                            className="w-full text-xs text-slate-500 hover:text-primary transition py-1.5 underline underline-offset-2"
                        >
                            Non funziona? Vedi le istruzioni manuali
                        </button>
                        {onDismiss && (
                            <button
                                type="button"
                                onClick={onDismiss}
                                data-testid={`${testIdPrefix}-continue`}
                                className="w-full text-xs text-slate-500 hover:text-slate-700 transition py-1.5"
                            >
                                Continua senza installare
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                            <div className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-2">Istruzioni manuali</div>
                            <ol className="space-y-1.5 text-[13px] text-slate-700">
                                <li className="flex gap-2">
                                    <span className="size-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">1</span>
                                    <span>Apri il menu di <span className="font-semibold">Samsung Internet</span>.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="size-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">2</span>
                                    <span>Seleziona <span className="font-semibold">"Apri in un'altra app"</span> o <span className="font-semibold">"Apri con"</span>.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="size-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">3</span>
                                    <span>Scegli <span className="font-semibold">Google Chrome</span>.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="size-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">4</span>
                                    <span>In Chrome seleziona <span className="font-semibold">"Installa app"</span> o <span className="font-semibold">"Aggiungi alla schermata Home"</span>.</span>
                                </li>
                            </ol>
                        </div>
                        <button
                            type="button"
                            onClick={onCopyLink}
                            data-testid={`${testIdPrefix}-copy-link`}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold text-sm py-2 hover:border-primary hover:text-primary transition"
                        >
                            {copied ? <>Link copiato ✓</> : <><Copy className="size-4" /> Copia il collegamento ufficiale</>}
                        </button>
                        <a
                            href={OFFICIAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full inline-flex items-center justify-center gap-2 text-xs text-slate-500 hover:text-primary transition py-1.5"
                        >
                            <ExternalLink className="size-3.5" /> {OFFICIAL_URL}
                        </a>
                        {onDismiss && (
                            <button
                                type="button"
                                onClick={onDismiss}
                                data-testid={`${testIdPrefix}-continue`}
                                className="w-full text-xs text-slate-500 hover:text-slate-700 transition py-1.5"
                            >
                                Continua senza installare
                            </button>
                        )}
                    </>
                )}
            </div>
        );
    }

    return null;
}

export function keyForMode(mode) {
    if (mode === "samsung") return SAMSUNG_KEY;
    if (mode === "ios") return IOS_KEY;
    return CHROME_KEY;
}

// Re-export the close icon so callers don't need lucide directly.
export { X };
